import {
    addBetMultiplierToLineBets,
    BetChangeActionCube,
    CubeActions,
    getFinalSymbols,
    isError,
} from "@spawn/backend-adapter-cube";
import { consumer, EventDispatcher, inject } from "@spawn/core";
import { parseNudgeNetworkState } from "@spawn/nudge";
import { generateRevealSymbolsOnBetChange, HighlightEvents, parseRevealNetworkState } from "@spawn/reveal";
import { invertNudgingOffsets, mapCalaverasToGoldenBonus } from "../hacks";
import { IGameNetworkState } from "../networkState";
import { INrgCubeGameResult } from "../types/cube";
import { UncoverTierService } from "../../services/uncoverTierService";

declare const window: Window &
	typeof globalThis & {
		__operatorAdapter?: { send: (event: string, data: Record<string, unknown>) => void };
	};

/** Adapter action for changings the bet against the cube backend. */
@consumer
export class ExtendedBetChangeActionCube extends BetChangeActionCube {
	@inject protected readonly uncoverTierService!: UncoverTierService;
	@inject protected readonly eventDispatcher!: EventDispatcher;

	public override async execute(...params: [number, number, boolean]): Promise<IGameNetworkState> {
		this.eventDispatcher.dispatch({ type: HighlightEvents.ResetHighlightFeature });

		const [betFactor, activeWinLines, setMaxBet] = params;
		if (!this.adapter.lastAllowedCommand.includes("maxBet") && setMaxBet) {
			return this.state as IGameNetworkState;
		}

		const response = await this.adapter.fetch().fetch<INrgCubeGameResult>({
			cmd: setMaxBet ? CubeActions.MaxBet : CubeActions.SetBetAndActiveWinlines,
			payload: { activeWinLines, bet: betFactor * activeWinLines },
		});

		if (isError(response)) {
			throw new Error(
				`Backend responded on ${setMaxBet ? CubeActions.MaxBet : CubeActions.SetBetAndActiveWinlines} with ${
					response.errorType
				}: ${response.errorMessage}`
			);
		}

		const {
			response: { transition },
		} = response;
		const bet = transition.bet / transition.slotGame.activeWinLines;
		// Engine 23.15.0 (OPA-11) sends this from BetChangeActionCube, which this action replaces.
		window.__operatorAdapter?.send("BetUpdate", { totalBet: transition.bet });

		const uncoverTier = this.uncoverTierService.fromWinFactor(this.state.slotGameBase.winFactor);

		const reveal = mapCalaverasToGoldenBonus(parseRevealNetworkState(transition.slotGame.nudgeAndReveal, uncoverTier));
		const nudging = invertNudgingOffsets(parseNudgeNetworkState(transition.slotGame.nudgeAndReveal));

		const finalSymbols = getFinalSymbols(transition.slotGame.reels, this.adapter);
		generateRevealSymbolsOnBetChange(finalSymbols, reveal, this.eventDispatcher);

		return {
			...this.state,
			slotGameBase: {
				...this.state.slotGameBase,
				reels: finalSymbols,
				selectedBet: bet,
				selectedLines: transition.slotGame.activeWinLines,
				availableBets: addBetMultiplierToLineBets(transition.lineBets.possibleValues, this.adapter.lineBetMultiplier),
				totalBet: transition.bet,
			},
			reveal,
			nudging,
		};
	}
}
